import { motion } from "framer-motion";
import { revealLeft, revealRight, Eyebrow, TextCta, VIEWPORT } from "./primitives";
import { useLanguage } from "@/i18n/LanguageContext";
import { PHYSICAL_PASSPORT_ENABLED } from "@/lib/config";

// Macro photography of the machined card in its leather sleeve. The object does
// not exist yet, so neither does the photograph: this must hold a real image
// before PHYSICAL_PASSPORT_ENABLED is ever turned on. A grey placeholder would
// destroy the exact quality this section exists to convey.
const PHYSICAL_PASSPORT_IMAGE = "";

/**
 * The Metal Passport, told as an object rather than a feature: no card, no
 * border, no hover lift, no glow. Photography, one paragraph, and silence.
 *
 * Renders nothing while PHYSICAL_PASSPORT_ENABLED is false — the object is a
 * vision, and production must never promise what it cannot deliver.
 */
export const PhysicalPassportSection = () => {
  const { t } = useLanguage();
  if (!PHYSICAL_PASSPORT_ENABLED || !PHYSICAL_PASSPORT_IMAGE) return null;

  return (
    <section
      id="passeport-metal"
      data-testid="physical-passport-section"
      className="relative scroll-mt-28 py-32 md:py-48 border-t border-ink/5"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-28 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealLeft}
        >
          <img
            src={PHYSICAL_PASSPORT_IMAGE}
            alt={t("physicalPassport.imageAlt")}
            loading="lazy"
            className="w-full aspect-[4/5] object-cover"
          />
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={VIEWPORT}
          variants={revealRight}
        >
          <Eyebrow>{t("physicalPassport.eyebrow")}</Eyebrow>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.15] tracking-tight mb-10">
            {t("physicalPassport.title")}
          </h2>
          <p className="text-base md:text-lg text-ink-muted leading-loose mb-12">
            {t("physicalPassport.body")}
          </p>
          <TextCta to="/passeport-metal" testId="physical-passport-cta">
            {t("physicalPassport.cta")}
          </TextCta>
        </motion.div>
      </div>
    </section>
  );
};

export default PhysicalPassportSection;
